import type { EntryDescriptor, EntryField } from './ClinicalEventEntry';
const choice = (key: string, label: string, values: string[]): EntryField => ({
  key,
  label,
  options: values.map((value) => ({ value, label: value })),
});
export const laborGroups: Record<string, EntryDescriptor[]> = {
  Admission: [{ eventType: 'NOTE.ADMISSION', label: 'Admission / place of care' }],
  'Fetal heart rate': [{ eventType: 'LABOR.FHR', label: 'Fetal heart rate' }],
  Contractions: [{ eventType: 'LABOR.CONTRACTIONS', label: 'Contractions' }],
  'Vaginal examination': [{ eventType: 'LABOR.PROGRESS', label: 'Vaginal examination' }],
  Vitals: [
    { eventType: 'VITALS.BP', label: 'Blood pressure' },
    { eventType: 'VITALS.PULSE', label: 'Pulse' },
    { eventType: 'VITALS.TEMP', label: 'Temperature' },
    { eventType: 'VITALS.RR', label: 'Respiratory rate' },
    { eventType: 'VITALS.SPO2', label: 'Oxygen saturation' },
  ],
  'Labor status': [
    {
      eventType: 'LABOR.STATUS',
      label: 'Labor status',
      fields: [
        choice('value', 'Status', [
          'Preadmission',
          'Admitted',
          'Active labor',
          'Second stage',
          'Third stage',
          'Fourth stage',
          'Discharged',
          'Transported',
        ]),
        { key: 'performed_by', label: 'Observed by' },
      ],
    },
  ],
  'Comfort / position': [
    { eventType: 'LABOR.COMFORT', label: 'Comfort' },
    { eventType: 'LABOR.POSITION', label: 'Maternal position' },
    { eventType: 'LABOR.FETAL_POSITION', label: 'Fetal position' },
  ],
  'Membranes / rupture': [
    { eventType: 'LABOR.MEMBRANES', label: 'Membranes' },
    { eventType: 'ROM', label: 'Rupture of membranes' },
  ],
  'Intake / output': [
    { eventType: 'IO.INTAKE', label: 'Intake' },
    { eventType: 'IO.OUTPUT', label: 'Output' },
  ],
  'Medication / therapy': [
    { eventType: 'MED.ADMIN', label: 'Medication' },
    { eventType: 'THERAPY', label: 'Therapy' },
  ],
  Milestone: [
    {
      eventType: 'MILESTONE',
      label: 'Milestone',
      fields: [
        choice('milestone', 'Milestone', [
          'LABOR_START',
          'ADMITTED',
          'ACTIVE_LABOR',
          'SECOND_STAGE',
          'PUSHING_START',
          'HEAD_BORN',
          'SHOULDERS_BORN',
          'BODY_BORN',
          'CROWN',
          'DISCHARGED',
          'TRANSPORTED',
        ]),
        { key: 'repeatable', label: 'Repeatable milestone', kind: 'boolean' },
        { key: 'performed_by', label: 'Observed by' },
        { key: 'nuchal_cord', label: 'Nuchal cord observed', kind: 'boolean' },
        { key: 'nuchal_detail', label: 'Nuchal cord details', kind: 'textarea' },
      ],
    },
  ],
  Birth: [{ eventType: 'BIRTH', label: 'Birth' }],
  Apgar: [
    {
      eventType: 'APGAR',
      label: 'Apgar score',
      fields: [
        { key: 'minute', label: 'Minutes after birth', kind: 'number' },
        ...['heart_rate', 'resp_effort', 'reflex', 'tone', 'color'].map((key) => ({
          key,
          label: key.replaceAll('_', ' '),
          options: [0, 1, 2].map((value) => ({ value, label: String(value) })),
        })),
      ],
    },
  ],
  Cord: [{ eventType: 'CORD', label: 'Umbilical cord' }],
  'First feeding': [{ eventType: 'NB.FEEDING', label: 'Newborn first feeding' }],
  Placenta: [{ eventType: 'PLACENTA', label: 'Placenta' }],
  Attendants: [
    { eventType: 'ATTENDANT.ARRIVAL', label: 'Attendant arrival' },
    { eventType: 'ATTENDANT.DEPARTURE', label: 'Attendant departure' },
  ],
  'Note / discharge': [
    { eventType: 'NOTE.PROGRESS', label: 'Progress note' },
    { eventType: 'NOTE.DISCHARGE', label: 'Discharge note' },
    { eventType: 'TRANSPORT', label: 'Transport' },
  ],
};
export const laborFullUpdate = [
  'Fetal heart rate',
  'Contractions',
  'Vaginal examination',
  'Vitals',
  'Comfort / position',
  'Membranes / rupture',
].flatMap((key) => laborGroups[key]);
